<template>
  <div class="bottom-sheet-overlay" @click.self="close">
    <div class="bottom-sheet">
      <!-- Header -->
      <div class="sheet-header">
        <h4>{{ actionTitle }}</h4>
      </div>

      <!-- Remark Input -->
      <div class="sheet-body">
        <label for="remark">Remark <span>*</span></label>
        <textarea
            id="remark"
            v-model="remark"
            maxlength="200"
            :placeholder="`Enter promotion ${isApprove ? 'approve' : 'reject'} remark...`"
            :class="['remark-textarea', isApprove ? 'approve-border' : 'reject-border']"
        />
        <div class="char-count">
          {{ remark.length }} / 200
        </div>
      </div>

      <!-- Actions -->
      <div class="sheet-actions">
        <button class="exit-btn" @click="close">Exit</button>
        <button
            :class="['confirm-btn', isApprove ? 'approve' : 'reject']"
            @click="confirmAction"
            :disabled="loading"
        >
          <span v-if="loading">
            <span :class="['spinner', isApprove ? 'approve-spinner' : 'reject-spinner']"></span>
            Processing...
          </span>
          <span v-else>
            Confirm
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { reviewPromotion } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "ConfirmPromotionAction",

  props: {
    action: { type: String, required: true },     // "approve" | "reject"
    promoId: { type: String, required: true }     // <-- receive promoId
  },

  data() {
    return {
      token: localStorage.getItem('token_set'), //Get the API access token from the local storage
      profileId: localStorage.getItem('profile_id'), //Get the logged user profileId from the local storage
      remark: "",
      loading: false,
    };
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  computed: {
    // Returns true if the current action is "approve"
    isApprove() {
      return this.action === "approve";
    },

    // Dynamically sets the title text based on the action
    // If approve → "Approve Promotion?"
    // Otherwise → "Reject Promotion?"
    actionTitle() {
      return this.isApprove
          ? "Approve Promotion?"
          : "Reject Promotion?";
    }
  },

  methods: {
    // Emit "close" event to notify parent to close the modal/sheet
    close() {
      this.$emit("close");
    },

    // Handle the review action (approve or reject promotion)
    async confirmAction() {
      try {
        // Validate if remark is provided
        if (!this.remark) {
          this.toast.error("Please add remark before confirm.");
        } else {
          // Set loading state to true (to show spinner/disable button, etc.)
          this.loading = true;

          //Build payload
          let payload = {};
          payload = {
            "status": this.action === 'approve' ? 1 : 2, //1 = Approve, 2 = Reject
            "review_remark": this.remark,
            "reviewed_by": this.profileId
          }

          //Call API for review promotion (approve/reject)
          const response = await reviewPromotion(this.promoId, this.token, payload);

          if (response.data.status === 'success') {
            this.toast.success(response.data.message);

            // Close the modal/sheet
            this.close();

            // Emit redirect event for parent component to handle redirection
            this.$emit("redirect");
          } else {
            this.toast.error(response.data.message);
          }
        }
      } catch (e) {
        // Handle unexpected errors and show failure toast
        this.toast.error(`Failed to ${this.action} this promotion`);
      } finally {
        // Always reset loading state after operation (success/failure)
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Overlay */
.bottom-sheet-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  z-index: 1100;
}

/* Sheet container */
.bottom-sheet {
  background: #fff;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 20px;
  animation: slideUp 0.3s ease;
}

/* Header */
.sheet-header{
  margin-bottom: 25px;
}

/* Body */
.sheet-body {
  margin-bottom: 16px;
}

/* Body - Label */
.sheet-body label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
  color: #333;
}

/* Body - Span (mandatory fields indicator) */
.sheet-body span {
  color: red;
}

/* Base textarea style */
.remark-textarea {
  width: 100%;
  min-height: 80px;
  resize: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;
}

/* Approve border */
.remark-textarea.approve-border:focus,
.remark-textarea.approve-border:not(:placeholder-shown) {
  border-color: #22c55e;
}

/* Reject border */
.remark-textarea.reject-border:focus,
.remark-textarea.reject-border:not(:placeholder-shown) {
  border-color: #ef4444;
}

/* Entered character lenght in the remark */
.char-count {
  text-align: left;
  font-size: 12px;
  margin-top: 4px;
  color: grey;
}

/* Actions */
.sheet-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Action Buttons - Confirm and Exit */
.sheet-actions button {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 14px;
}

/* Exit button */
.exit-btn {
  background-color: #e5e5e5;
  color: #333;
}

/* Confirm button variations */
.confirm-btn.approve {
  background-color: #22c55e;
  color: #fff;
}
.confirm-btn.reject {
  background-color: #ef4444;
  color: #fff;
}

/* Slide-up animation */
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* Base spinner */
.spinner {
  border: 3px solid #f3f3f3; /* Light gray */
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

/* Approve spinner (green) */
.approve-spinner {
  border-top: 3px solid #22c55e;
}

/* Reject spinner (red) */
.reject-spinner {
  border-top: 3px solid #ef4444;
}

/* Loading animation */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>